import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Box, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { Offer } from '@luxuryescapes/contract-svc-offer';

import { addHours, formatDateSlashesWithClock, isBefore, sortByDate } from '~/services/TimeService';
import UsersService from '~/services/UsersService';
import { customerPortalHost } from '~/services/common';

interface DeepLink extends Offer.OfferDeepLink {
  customerEmail?: string;
  approverEmail?: string;
  offerName?: string;
  offerSlug?: string;
}

interface OfferDeepLinksPageProps {
  deepLinksList: Array<DeepLink>;
  deepLinksListCount: number;
  refetchDeepLinks?: (page: number) => void;
  loading: boolean;
  errors: string | null;
}

function OfferDeepLinksPage({
  deepLinksList,
  deepLinksListCount,
  refetchDeepLinks,
  loading,
  errors,
}: OfferDeepLinksPageProps) {
  const tenant = useSelector((state: App.State) => state.tenant);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<Utils.FetchingState>('idle');
  const [pageIndex, setPageIndex] = useState(0);

  const onPageChange = useCallback(
    (page: number) => {
      setPageIndex(page);
      refetchDeepLinks(page);
    },
    [refetchDeepLinks],
  );

  const isFetching = useCallback(() => {
    return isLoading === 'loading';
  }, [isLoading]);

  const handleCopyDeepLink = async (deepLink: DeepLink) => {
    if (deepLink.hash) {
      setIsLoading('loading');
      const offerLink = `/offer/${deepLink.offerSlug}/${deepLink.offerId}`;
      const baseUrl = `${customerPortalHost(tenant)?.replace('/au', ``)}/${deepLink?.region ?? 'au'}`;
      const fullUrl = `${baseUrl}${offerLink}?offerDeepLinkHash=${deepLink.hash}`;
      const spoofLink = await UsersService.spoofUserWithOfferDeepLink(deepLink.customerId, tenant, fullUrl);

      navigator.clipboard
        .writeText(spoofLink)
        .then(() => {
          enqueueSnackbar('Deep link copied to clipboard!', { variant: 'success' });
          setIsLoading('success');
        })
        .catch((err) => {
          console.error('Failed to copy deep link:', err);
          setIsLoading('failed');
        });
    }
  };

  const columns: Array<GridColDef> = [
    {
      field: 'copyDeepLink',
      headerName: 'Copy',
      minWidth: 60,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Copy Deep Link">
          <IconButton
            size="small"
            onClick={() => handleCopyDeepLink(params.row)}
            disabled={!params.row.hash || isFetching()}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'offerId',
      headerName: 'Offer ID',
      minWidth: 175,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Link href={`/offers/${params.row.offerId}`} target="_blank">
          {params.row.offerId}
        </Link>
      ),
    },
    {
      field: 'offerName',
      headerName: 'Offer Name',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'orderId',
      headerName: 'Booking status',
      minWidth: 175,
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        params.row.status === 'booked' ? (
          <Typography variant="body2">
            <Typography component="span" fontWeight="bold">
              Booked:{' '}
            </Typography>{' '}
            <Link href={`/purchases/${params.row.orderId}`} target="_blank">
              {params.row.orderId}
            </Link>
          </Typography>
        ) : (
          <Typography
            variant="body2"
            color={isBefore(addHours(1, params.row.createdAt)) ? 'error.main' : 'text.primary'}
          >
            <Typography component="span" fontWeight="bold">
              Exp:{' '}
            </Typography>{' '}
            {formatDateSlashesWithClock(addHours(1, params.row.createdAt))}
          </Typography>
        ),
    },
    {
      field: 'region',
      headerName: 'Region',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'customerEmail',
      headerName: 'Customer Email',
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Link href={`/users/${params.row.customerId}`} target="_blank">
          {params.row.customerEmail}
        </Link>
      ),
    },
    {
      field: 'agentEmail',
      headerName: 'Agent Email',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'details',
      headerName: 'Details',
      minWidth: 300,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <Typography component="span" fontWeight="bold">
                Approver:
              </Typography>{' '}
              <Link href={`/users/${params.row.approverId}`} target="_blank">
                {params.row.approverEmail ? params.row.approverEmail : params.row.approverId}
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <Typography component="span" fontWeight="bold">
                Case Number:
              </Typography>{' '}
              {params.row.caseNumber}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <Typography component="span" fontWeight="bold">
                Opening Reason:
              </Typography>{' '}
              {params.row.openingReason}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <Typography component="span" fontWeight="bold">
                Sales Attribution:
              </Typography>{' '}
              {params.row.salesAttribution}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <Typography component="span" fontWeight="bold">
                Notes:
              </Typography>{' '}
              {params.row.notes}
            </Typography>
          </Stack>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      minWidth: 180,
      flex: 1,
      type: 'date',
      sortComparator: sortByDate,
      valueFormatter: (value) => formatDateSlashesWithClock(value),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      {errors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors}
        </Alert>
      )}
      <DataGrid
        rows={deepLinksList}
        rowCount={deepLinksListCount}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: pageIndex },
          },
        }}
        getRowHeight={() => 'auto'}
        paginationMode="server"
        paginationModel={{ page: pageIndex, pageSize: 25 }}
        onPaginationModelChange={({ page }) => onPageChange(page)}
        disableRowSelectionOnClick
        loading={loading}
        getRowId={(row) => row.offerId + row.customerId + row.createdAt}
        autoHeight
        sx={{
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            padding: 1,
          },
        }}
      />
    </Box>
  );
}

export default OfferDeepLinksPage;
